import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Send, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

// Store reset tokens in localStorage for mock implementation
const RESET_TOKENS_KEY = 'votebeats_reset_tokens';

function generateResetToken(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

function storeResetToken(email: string, token: string) {
  const tokens = JSON.parse(localStorage.getItem(RESET_TOKENS_KEY) || '{}');
  tokens[token] = {
    email,
    createdAt: Date.now(),
    expiresAt: Date.now() + 3600000, // 1 hour expiry
  };
  localStorage.setItem(RESET_TOKENS_KEY, JSON.stringify(tokens));
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await resetPassword(email);

      // Generate and store mock reset token
      const token = generateResetToken();
      storeResetToken(email, token);
      setResetToken(token);

      setSubmitted(true);
      toast.success('Reset instructions sent!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-success" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Check Your Email</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            We've sent password reset instructions to <strong>{email}</strong>
          </p>
        </div>

        {/* Mock: Show reset link for testing */}
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-left">
          <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-2">
            🧪 Test Mode: Reset Link
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
            In production, this would be sent via email. For testing:
          </p>
          <Link
            to={`/reset-password?token=${resetToken}`}
            className="text-sm text-primary hover:underline break-all"
          >
            Click here to reset your password
          </Link>
        </div>

        <Link to="/" className="btn btn-secondary inline-flex items-center">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Sign In
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reset Password</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Enter your email and we'll send you reset instructions
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div role="alert" className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input pl-10"
              required
              disabled={loading}
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Send Reset Link
            </>
          )}
        </button>
      </form>

      <p className="text-center">
        <Link to="/" className="text-sm text-primary hover:text-primary-hover inline-flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Sign In
        </Link>
      </p>
    </div>
  );
}
